import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { CertificatesFilters } from 'components/pages/certificates/useCertificatesPageState';
import { PreviewContext } from 'contexts/previewContext';
import { certificatesMock } from 'mocks/certificatesMock';
import { applyPreviewProductToList } from 'mocks/applyPreviewProduct';
import type { StudioPreviewProduct } from '@repo/types';
import { useContext } from 'react';
import { createApiCall } from 'services';
import { Certificates, CertificatesFilter } from 'store/certificates/model';
import { CertificateCustomer } from 'types/Certificate';

interface UseGetCertificatesParams extends CertificatesFilters {}

type useGetCertificatesResponse = Certificates;

const getCertificates = async (
  params: UseGetCertificatesParams,
  isPreview: boolean,
  previewProduct: StudioPreviewProduct | null
): Promise<useGetCertificatesResponse> => {
  if (isPreview) {
    return {
      // VRG-827 — the FIRST row only. See `applyPreviewProductToList`: rewriting all
      // seven to one product makes the list preview worse, not better.
      data: applyPreviewProductToList(
        certificatesMock,
        previewProduct
      ) as unknown as CertificateCustomer[],
      filter: {} as unknown as CertificatesFilter,
      limit: 0,
      offset: 0,
      total: 0,
    };
  }

  const tab = params.tab;
  const derivedStatus = mapTabToStatus(tab);
  const uiSortBy = params.sort_by;
  const uiSortOrder = params.sort;
  const backendSortBy = mapUiSortKeyToBackend(uiSortBy);

  const allowedApiTabs = new Set(['all', 'active', 'transfer', 'lost', 'stolen', 'cancel request']);
  const incomingTab = params.tab;
  const normalizedTab = allowedApiTabs.has(String(incomingTab)) ? incomingTab : 'all';

  /*
   * `sort_by` is stripped before the spread, then re-added ONLY as a key the
   * server accepts.
   *
   * The server whitelists three values (CertificatesOrderKey: created_at,
   * timestamp, description). `mapUiSortKeyToBackend` deliberately returns
   * undefined for the UI-only keys that are sorted on the client instead — but
   * `...params` had already put the RAW ui key into the request, and a
   * conditional spread can only override, never remove. So every client-side
   * sort key reached the API verbatim and came back "Validation errors": that
   * was true of `status` before `owned_since` was ever added, which is why
   * sorting the list by Status showed an error state rather than a sorted list.
   */
  const { sort_by: _uiSortByRaw, ...paramsWithoutSort } = params as Record<string, unknown>;

  const apiCallParams = {
    ...paramsWithoutSort,
    tab: normalizedTab,
    ...(derivedStatus ? { statuses: derivedStatus.split(',') } : {}),
    ...(backendSortBy ? { sort_by: backendSortBy } : {}),
    ...(uiSortOrder ? { sort: uiSortOrder } : {}),
  };

  const response = await createApiCall({
    method: 'GET',
    url: `/certificates/customer`,
    params: apiCallParams,
  });

  // VERA-837 fix: `createApiCall`'s error handler returns the raw axios error
  // response (or a serialized network error) instead of throwing, so a
  // 401/403 response body (`{message, code, details}`) was cast straight
  // through as if it were the success payload (`{data, total, ...}`).
  // `certificates` then resolved to `undefined`, which the page's `isEmpty`
  // check treats identically to a genuine zero-certificate account — an
  // authorization failure rendered as "No certificates yet". Throw here so
  // the query enters an error state the page can distinguish from empty.
  const status = (response as {status?: number} | undefined)?.status;
  if (typeof status !== 'number' || status < 200 || status >= 300) {
    // A missing `status` means the request never got an HTTP response at all
    // (API unreachable, DNS/network failure, timeout — `createApiCall`'s
    // errorHandler returns the raw serialized axios error in that case, which
    // has no `.status`). That's a connectivity problem, not anything about
    // this account's certificates, and must not be worded like one — it was
    // previously falling into the same vague "We could not load your
    // certificates" copy as a genuine 401/403, implying a data/account issue
    // that isn't real.
    // 401/403 take PRECEDENCE over the server's own message, and that ordering
    // is the fix. The server's string was consulted first, and for an auth
    // failure NestJS supplies its built-in ForbiddenException default —
    // "Forbidden resource" — which is what the customer then read on the page.
    // It is not our copy, it names nothing the reader can act on, and it is the
    // literal internal phrase a guard emits when `canActivate` returns false.
    const isAuthFailure = status === 401 || status === 403;
    // A 5xx gets OUR copy, never the server's `data.message`. This is the same
    // defect the 401 branch above was created to fix, one status class over:
    // that fix exists because NestJS's built-in "Forbidden resource" was reaching
    // the customer verbatim, and a 500 reaches them the same way — the observed
    // string is literally "Internal server error", which is not our voice, names
    // nothing the reader can act on, and is the framework's internal phrase.
    //
    // It must also not borrow the CONNECTION copy: we received an HTTP response,
    // so the network demonstrably works, and telling someone to check their wifi
    // over a server fault sends them to fix something that isn't broken. "On our
    // end" is the whole point of the sentence.
    //
    // 4xx below keeps the passthrough: a client-error message can carry a
    // specific, actionable reason, whereas a 5xx never does.
    const isServerFault = typeof status === 'number' && status >= 500;
    const message = typeof status !== 'number'
      ? "We couldn't connect. Please check your connection and try again."
      : isAuthFailure
        ? 'Your session has expired. Sign in again to see your certificates.'
        : isServerFault
          ? 'Something went wrong on our end. Please try again.'
          : (response as {data?: {message?: string}} | undefined)?.data?.message ||
            'We could not load your certificates right now. Please try again.';
    const requestError = new Error(message) as Error & {status?: number};
    requestError.status = status;
    throw requestError;
  }

  return response.data as useGetCertificatesResponse;
};

export const useGetCertificates = (
  params: UseGetCertificatesParams
): UseQueryResult<useGetCertificatesResponse, Error> => {
  const { isPreview, previewProduct } = useContext(PreviewContext);

  return useQuery({
    // VRG-827 — the selected product is part of the key for the same reason it is in
    // `useGetCertificatePublic`: the preview branch reads no other input that changes.
    queryKey: ['certificates', params, previewProduct?.id ?? null],
    queryFn: () => getCertificates(params, isPreview, previewProduct),

    /*
     * KEEP THE ROWS ON SCREEN WHILE A NEW FILTER/SORT LOADS.
     *
     * `params` is part of the query key, so every filter, sort, search, tab and
     * page change asks for a key that has never been fetched. Without this, such
     * a key starts at `status: 'pending'` with no data, `isLoading` goes true,
     * and the list page swaps the whole grid for the skeleton until the response
     * lands — MEASURED: applying a filter replaced the grid element outright,
     * with one skeleton appearance in between. That is the "flash" reported on
     * both selecting a sort field and applying a filter; they are one bug, since
     * both do nothing but change this key.
     *
     * With `keepPreviousData` the previous rows stay mounted and `isLoading`
     * stays FALSE for a key change, so the grid is never torn down. The new set
     * replaces the old in place when it arrives.
     *
     * ## Why this does not re-open the flicker the poll caused
     *
     * `refetchInterval` below re-fetches the SAME key every 20s. A same-key
     * refetch sets `isFetching` / `isRefetching` but NOT `isPlaceholderData` —
     * placeholder state means "this data belongs to a different key". That
     * distinction is what lets the grid dim on a real filter change and stay
     * completely still through every background poll, which the older
     * `isRefetching` signal could not express (see the skeleton gate in
     * `components/pages/certificates/index.tsx`).
     */
    placeholderData: keepPreviousData,
    // Auth/authorization failures aren't transient — don't burn 3 retries
    // (react-query's default) before surfacing the error to the user.
    retry: false,

    // VERA-304 — poll so a gift ARRIVES rather than waiting for the recipient to
    // navigate. The case that sets the interval is two people standing together:
    // one sends, and the other is looking at their phone. Anything slower reads
    // as "it didn't work" and they start retrying.
    //
    // There is no push channel (no websocket/SSE), so refetch-on-focus and
    // refetch-on-mount were the only triggers — and neither fires for a recipient
    // who is already sitting ON the list with the app in front of them, which is
    // exactly the moment that matters.
    // 20s, RELAXED FROM 10s once pull-to-refresh landed (`components/UI/PullToRefresh`).
    // The interval only has to cover the PASSIVE case now — someone with the list open
    // who does nothing. The impatient case, which is what argued for 10s, is served
    // better by the gesture: a pull is an explicit "I want it now", which is strictly
    // better information than a timer's guess, and it is instant rather than averaging
    // half the interval. Returning to the app is already covered by the global
    // `refetchOnWindowFocus: 'always'`.
    //
    // That halves background request volume per open app — measured at ~25 kB per poll,
    // so the saving is real at scale and pays for nothing the user can perceive.
    refetchInterval: 20_000,
    // FOREGROUND ONLY. This is the default, but it is stated explicitly because
    // it is the entire cost argument: a backgrounded app polling every 10s is a
    // request every 10s per install, forever, for a screen nobody is looking at.
    // With it false the timer is suspended while the tab/app is hidden, and the
    // global `refetchOnWindowFocus: 'always'` (see providers/queryClient) covers
    // the return — so coming back is still instant without paying while away.
    refetchIntervalInBackground: false,
  });
};

const mapUiSortKeyToBackend = (key: string | null): string | undefined => {
  if (!key) return undefined;

  switch (key) {
    // Sorted on the client — the server has no column for these, so they must
    // resolve to undefined AND be kept out of the request entirely (see above).
    case 'status':
      return undefined;
    case 'owned_since':
      return undefined;
    case 'name':
      return 'description';
    case 'last_activity':
      return 'timestamp';
    default:
      return key;
  }
};

const mapTabToStatus = (tab?: string): string | undefined => {
  switch (tab) {
    case 'active':
      return 'active';
    case 'transfer':
      return 'transfer_initiated,transfer_waiting_confirmation,waiting_accept';
    case 'stolen':
      return 'stolen';
    case 'cancel request':
      return 'cancel_request';
    case 'lost':
      return 'lost';
    default:
      return undefined;
  }
};
